import { useCallback, useEffect, useState } from "react";

/**
 * SSR-sikker dismiss-hook. Læser localStorage efter mount (aldrig i
 * useState-initializer) for at undgå hydration-mismatch.
 *
 * Returnerer:
 *   ready     – true når localStorage er læst (brug til at undgå flash)
 *   dismissed – true hvis brugeren har lukket blokken
 *   dismiss() – markér som lukket
 *   restore() – vis igen
 */
export function useDismissed(key: string) {
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(key) === "1");
    } catch {
      // localStorage kan være blokeret; vi behandler det som "ikke lukket".
    }
    setReady(true);
  }, [key]);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, [key]);

  const restore = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setDismissed(false);
  }, [key]);

  return { ready, dismissed, dismiss, restore };
}
